import { Component, EventEmitter, HostBinding, Input, Output, OnInit } from '@angular/core';
import { NavItem } from './nav-item';
import { animate, state, style, transition, trigger } from '@angular/animations';

/**
 * A single sidebar menu row (used by the Summary page side navigation).
 * Renders itself recursively for `menuItem.children`, increasing `depth` per level.
 *
 * Interaction model:
 * - Clicking a row selects that tab (and opens its children if it has any).
 * - Only the trailing chevron collapses/expands a section, so selecting a
 *   parent never hides its children and the menu height stays stable.
 */
@Component({
    selector: 'app-menu-list-item',
    templateUrl: './menu-list-item.component.html',
    styleUrls: ['./menu-list-item.component.scss'],
    animations: [
        trigger('indicatorRotate', [
            state('collapsed', style({ transform: 'rotate(0deg)' })),
            state('expanded', style({ transform: 'rotate(180deg)' })),
            transition('expanded <=> collapsed',
                animate('225ms cubic-bezier(0.4,0.0,0.2,1)')
            ),
        ])
    ]
})
export class MenuListItemComponent implements OnInit {
    // Inputs are always provided by the parent template, hence the definite-assignment (!)
    @Input() menuItem!: NavItem;
    // Nesting level: 0 = top-level item, 1 = sub-item (defaulted here instead of in the constructor)
    @Input() depth: number = 0;
    @Input() selectedTab!: string;
    // True when the sidebar is the icon-only rail; enables the name tooltip (labels are hidden then)
    @Input() isRailCollapsed: boolean = false;
    @Output() selectTab = new EventEmitter<string>();
    protected isShowChild: boolean = true;
    protected isExpanded = true;
    @HostBinding('attr.aria-expanded') ariaExpanded = this.isShowChild;
    // CSS custom properties consumed by the stylesheet; assigned in ngOnInit once `depth` is known
    @HostBinding('style.--padding-left') paddingLeft!: string;
    @HostBinding('style.--icon-size') iconSize!: string;
    @HostBinding('style.--font-size') fontSize!: string;

    ngOnInit() {
        this.ariaExpanded = this.isShowChild;
        this.paddingLeft = `${this.depth * 20}px`;
        this.iconSize = `${24 - this.depth * 6}px`;
        this.fontSize = `${16 - this.depth * 2}px`;
    }

    /**
     * Selects a tab and bubbles the id up to the parent menu / Summary page.
     * Previously this also toggled the children, which collapsed the section on a
     * second click and made the menu jump; now a parent click only ever opens it.
     */
    onSelectTab(menuItemId: string) {
        this.selectedTab = menuItemId;
        if (this.menuItem.id === menuItemId && this.menuItem.children) {
            this.isShowChild = true;
        }
		this.selectTab.emit(menuItemId);
	}

    /**
     * Chevron click: collapse/expand this item's children without changing the
     * selected tab. stopPropagation keeps the row's own (click) from firing.
     */
    toggleChildren(event: MouseEvent) {
        event.stopPropagation();
        this.isShowChild = !this.isShowChild;
    }
}
